import React from 'react';
import { Helmet } from 'react-helmet-async';
import { company, founders } from '../config/company';

interface SEOProps {
  title: string;
  description: string;
  imagePath?: string;
  keywords?: string;
  author?: string;
  type?: string;
  /**
   * Absolute canonical URL for this page. Defaults to the current location on
   * the canonical company domain so each page is canonical to itself rather
   * than every page pointing at the site root.
   */
  url?: string;
  publishedTime?: string;
  modifiedTime?: string;
  section?: string;
  tags?: string[];
}

const SITE_NAME = company.brandName;
const SITE_URL = company.url;

const normalizeCanonical = (value: string): string => {
  if (value.length > SITE_URL.length && value.endsWith('/')) {
    return value.slice(0, -1);
  }
  return value;
};

const currentCanonical = (): string => {
  if (typeof window === 'undefined') return SITE_URL;
  const path = window.location.pathname || '/';
  return normalizeCanonical(`${SITE_URL}${path === '/' ? '' : path}`);
};

const SEO: React.FC<SEOProps> = ({
  title,
  description,
  imagePath = '/images/esanza/icon.png',
  keywords = 'agricultural operating system, farm management software, AI agriculture platform, AI agronomy assistant, farm traceability software, agricultural supply chain, B2B agricultural marketplace, digital agriculture',
  author = company.brandName,
  type = 'website',
  url,
  publishedTime,
  modifiedTime,
  section,
  tags = []
}) => {
  const fullTitle = title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`;
  const canonicalUrl = normalizeCanonical(url || currentCanonical());
  const imageUrl = imagePath.startsWith('http') ? imagePath : `${SITE_URL}${imagePath}`;

  // Structured Data for Organization. Founders are real people from the
  // canonical team config — never the company itself.
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": company.brandName,
    "legalName": company.legalName,
    "url": SITE_URL,
    "logo": `${SITE_URL}/images/esanza/icon.png`,
    "description": "eSanza is an AI-enabled agricultural operating system connecting farm management, field intelligence, traceability, supply-chain operations and B2B market access in one platform.",
    "foundingLocation": {
      "@type": "Place",
      "address": {
        "@type": "PostalAddress",
        "addressCountry": company.originCountryCode
      }
    },
    "founders": founders.map((founder) => ({
      "@type": "Person",
      "name": founder.name,
      "jobTitle": founder.role
    })),
    "address": {
      "@type": "PostalAddress",
      "addressCountry": company.originCountryCode
    },
    "contactPoint": {
      "@type": "ContactPoint",
      "contactType": "partnerships",
      "email": company.publicEmail
    },
    "areaServed": "Worldwide",
    "knowsAbout": [
      "Farm management software",
      "Agricultural field intelligence",
      "Farm traceability software",
      "Agricultural supply chain",
      "B2B agricultural marketplace",
      "Agricultural logistics",
      "Farm analytics"
    ]
  };

  // Structured Data for the software product. No pricing offer is published
  // because there is no verified public price.
  const softwareSchema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": company.brandName,
    "applicationCategory": "BusinessApplication",
    "operatingSystem": "Web, Android",
    "url": SITE_URL,
    "description": "The intelligent operating system for agriculture: farm management, field intelligence, traceability, supply-chain operations and B2B market access.",
    "publisher": {
      "@type": "Organization",
      "name": company.legalName
    }
  };

  // Structured Data for Website. No SearchAction: the public site has no
  // /search route, so advertising one would be inaccurate.
  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": SITE_NAME,
    "url": SITE_URL,
    "description": description,
    "publisher": {
      "@type": "Organization",
      "name": company.legalName
    }
  };

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content={author} />
      <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      <meta name="googlebot" content="index, follow" />
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={title} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="en_US" />
      <meta property="og:locale:alternate" content="fr_FR" />

      {publishedTime && <meta property="article:published_time" content={publishedTime} />}
      {modifiedTime && <meta property="article:modified_time" content={modifiedTime} />}
      {section && <meta property="article:section" content={section} />}
      {tags.map(tag => (
        <meta key={tag} property="article:tag" content={tag} />
      ))}

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={imageUrl} />
      <meta name="twitter:image:alt" content={title} />

      {/* Additional Meta Tags */}
      <meta name="theme-color" content="#06150e" />
      <meta name="msapplication-TileColor" content="#06150e" />
      <meta name="application-name" content={SITE_NAME} />
      <meta name="apple-mobile-web-app-title" content={SITE_NAME} />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
      <meta name="mobile-web-app-capable" content="yes" />
      <meta name="format-detection" content="telephone=no" />

      {/* Language and Content. Market scope is worldwide; the company is
          registered in Rwanda — the origin is an attribute, not the market. */}
      <meta httpEquiv="content-language" content="en-us" />
      <meta name="language" content="English" />
      <meta name="coverage" content="Worldwide" />
      <meta name="distribution" content="Global" />
      <meta name="rating" content="General" />

      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(organizationSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(websiteSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(softwareSchema)}
      </script>

      {/* Preconnect to external domains */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      <link rel="preconnect" href="https://api.esanza.com" />

      {/* DNS Prefetch */}
      <link rel="dns-prefetch" href="//fonts.googleapis.com" />
      <link rel="dns-prefetch" href="//fonts.gstatic.com" />
      <link rel="dns-prefetch" href="//api.esanza.com" />

      {/* Favicon and Icons */}
      <link rel="icon" type="image/x-icon" href="/favicon.ico" />
      <link rel="icon" type="image/png" sizes="32x32" href="/favicon-32x32.png" />
      <link rel="icon" type="image/png" sizes="16x16" href="/favicon-16x16.png" />
      <link rel="apple-touch-icon" sizes="180x180" href="/apple-touch-icon.png" />
      <link rel="manifest" href="/manifest.json" />

      {/* Additional SEO enhancements */}
      <meta name="referrer" content="origin-when-cross-origin" />
      <meta name="color-scheme" content="dark light" />
      <meta name="supported-color-schemes" content="dark light" />
    </Helmet>
  );
};

export default SEO;
